import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import Card from 'reactstrap/lib/Card';
import classnames from 'classnames';
import {
  IncentivesCreativeConfigDefaultProps,
  IncentivesCreativeConfigPropTypes,
} from 'client/data/models/native-ads-creative-config';
import { DfpTracking } from 'client/site-modules/shared/components/native-ad/utils/dfp-tracking';
import { AdLabel } from 'client/site-modules/shared/components/native-ad/native-ad-components/ad-label/ad-label';
import { Disclaimer } from 'client/site-modules/shared/components/native-ad/native-ad-components/disclaimer/disclaimer';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { addZipCode, definePlatform } from 'site-modules/shared/components/native-ad/utils/utils';
import {
  SiteServedAdDefaultProps,
  SiteServedAdPropTypes,
} from 'client/site-modules/shared/components/ad-unit/ad-unit-prop-types';
import { INCENTIVES_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import { siteServedAdTrackingWrapper } from 'site-modules/shared/components/native-ad/site-served-ad-tracking-wrapper/site-served-ad-tracking-wrapper';
import { CtaButtonText } from 'site-modules/shared/components/native-ad/native-ad-components/cta-button-text/cta-button-text';

import './comparator-incentives-site-served-ad.scss';

export function ComparatorIncentivesSiteServedAdUI({
  adRef,
  adClickTrackingListener,
  adLoadTrackingListener,
  adTrackers,
  mobile,
  position,
  currentVehicle,
  creativeConfig,
  ads11245Enabled,
}) {
  const {
    siteServedAdsUpdater,
    zipCode,
    creativeConfigData: {
      logo,
      linkDisclaimer,
      disclaimerCopy,
      headline,
      headline2,
      linkDisplayUrl,
      cta,
      lineItemId,
      siteServedCreativeId,
      target,
    },
    traffickingData: { clickTracker },
  } = creativeConfig;

  const loadTrackingListener = useCallback(
    event => {
      adLoadTrackingListener(event, cta);
    },
    [adLoadTrackingListener, cta]
  );

  if (!logo || !linkDisplayUrl || !cta) {
    return null;
  }

  const defaultHeadline = `Find Deals Near ${zipCode}`;
  const processedHeadline = addZipCode(headline, zipCode) || defaultHeadline;
  const ctaURL = addZipCode(clickTracker[definePlatform(mobile)], zipCode);
  const ctaTarget = target || '_blank';

  const nativeStyle = INCENTIVES_AD.NATIVE_STYLE;

  return (
    <Card
      className={classnames('comparator-incentives-site-served pos-r w-100', {
        'bg-gray-lightest': !ads11245Enabled,
        'gradient-bg-ads pt-0_25 h-100': ads11245Enabled,
      })}
      data-tracking-parent={nativeStyle}
      innerRef={adRef}
      style={{ overflow: 'hidden' }}
    >
      <DfpTracking
        lineItemId={lineItemId}
        siteServedCreativeId={siteServedCreativeId}
        mobile={mobile}
        nativeStyle={nativeStyle}
        position={position}
        currentVehicle={currentVehicle}
        slotRenderEndListener={loadTrackingListener}
        siteServedAdsUpdater={siteServedAdsUpdater}
      />
      {adTrackers}
      <a
        className="site-served-ad-link no-focus text-decoration-none d-block"
        href={ctaURL}
        target={ctaTarget}
        rel="noopener noreferrer"
        onClick={adClickTrackingListener}
      >
        <AdLabel />
        <div className="p-0_5 px-md-1">
          <div className="headline fw-bold text-gray-darkest mb-0_25 size-16 pe-1">
            <ContentFragment>{processedHeadline}</ContentFragment>
          </div>

          {!mobile && (
            <div className="headline2 medium text-gray-darker">
              <ContentFragment>{headline2}</ContentFragment>
            </div>
          )}

          <div className="d-flex flex-row mb-0_5 mt-0_75">
            <div
              className={classnames('w-100 d-flex cta-link flex-wrap justify-content-center', {
                'is-mobile w-100': mobile,
              })}
            >
              <span
                className={classnames('btn btn-sm py-0_5 px-0_75 mx-auto btn-blue-50 medium text-transform-none', {
                  'cta w-100': mobile,
                })}
              >
                <CtaButtonText text={cta} />
              </span>
              <div className="oem-url disclaimer ms-0_25 mt-0_25 my-lg-auto">{linkDisplayUrl}</div>
            </div>
          </div>
        </div>
      </a>
      {disclaimerCopy && <Disclaimer linkDisclaimer={linkDisclaimer} disclaimerText={disclaimerCopy} />}
    </Card>
  );
}

ComparatorIncentivesSiteServedAdUI.propTypes = {
  ...SiteServedAdPropTypes,
  creativeConfig: IncentivesCreativeConfigPropTypes,
  ads11245Enabled: PropTypes.bool,
};

ComparatorIncentivesSiteServedAdUI.defaultProps = {
  ...SiteServedAdDefaultProps,
  creativeConfig: IncentivesCreativeConfigDefaultProps,
  ads11245Enabled: false,
};

export const ComparatorIncentivesSiteServedAd = siteServedAdTrackingWrapper(ComparatorIncentivesSiteServedAdUI);
